/**
 * Global workspace-search state: the ⌘F / Ctrl+F binding, the open flag,
 * and the model-derived entry index. Shared by the viewer App and the
 * workbench's SearchPalette primitive so both surfaces get the identical
 * search UX from one implementation.
 *
 * The hotkey overrides the browser's in-page find on purpose — that would
 * scan the rendered DOM (mostly invisible SVG transforms) instead of the
 * architecture model the user expects to be searching. Whoever mounts this
 * owns ⌘F: pass `hotkey={false}` to any `WorkspaceNav` in the same
 * composition or both will fight over the key.
 */
import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import type { ArchitectureModel } from "@archlang/scene/arch-model";
import { buildSearchEntries, type SearchEntry } from "./searchIndex.js";

export interface SearchModalState {
  readonly searchOpen: boolean;
  readonly setSearchOpen: Dispatch<SetStateAction<boolean>>;
  /** Every navigable entity, flattened + rankable. Rebuilt on model change. */
  readonly entries: readonly SearchEntry[];
}

/** @param bindHotkey set false when another instance owns ⌘F (a palette
 *  handed external `state` must not register a second listener). */
export function useSearchModal(
  model: ArchitectureModel | null,
  bindHotkey = true,
): SearchModalState {
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    if (!bindHotkey) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "f") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [bindHotkey]);

  const entries = useMemo(() => buildSearchEntries(model), [model]);

  return { searchOpen, setSearchOpen, entries };
}
